import React from 'react';
import {FormattedMessage, injectIntl, intlShape, defineMessages} from 'react-intl';
import {connect} from 'react-redux';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import bindAll from 'lodash.bindall';
import styles from './loader.css';
import {getIsLoadingWithId} from '../../reducers/project-state';

const mainMessages = {
    'gui.loader.headline': (
        <FormattedMessage
            defaultMessage="Loading Project"
            description="Main loading message"
            id="gui.loader.headline"
        />
    ),
    'gui.loader.creating': (
        <FormattedMessage
            defaultMessage="Creating Project"
            description="Main creating message"
            id="gui.loader.creating"
        />
    )
};

const messages = defineMessages({
    projectData: {
        defaultMessage: 'Loading project …',
        description: 'Appears when loading project data, but not assets yet',
        id: 'tw.loader.projectData'
    },
    downloadingAssets: {
        defaultMessage: 'Downloading assets ({complete}/{total}) …',
        description: 'Appears when loading project assets from a project on a remote website',
        id: 'tw.loader.downloadingAssets'
    },
    loadingAssets: {
        defaultMessage: 'Loading assets ({complete}/{total}) …',
        description: 'Appears when loading project assets from a project file on the user\'s computer',
        id: 'tw.loader.loadingAssets'
    }
});

// Because progress events are fired so often during the very performance-critical loading
// process and React updates are very slow, we bypass React for updating the progress bar.

class LoaderComponent extends React.Component {
    constructor (props) {
        super(props);
        bindAll(this, [
            'handleAssetProgress',
            'handleProjectLoaded',
            'barInnerRef',
            'messageRef'
        ]);
        this.barInnerEl = null;
        this.messageEl = null;
        this.ignoreProgress = false;
    }
    componentDidMount () {
        this.handleAssetProgress(
            this.props.vm.runtime.finishedAssetRequests,
            this.props.vm.runtime.totalAssetRequests
        );
        this.props.vm.on('ASSET_PROGRESS', this.handleAssetProgress);
        this.props.vm.runtime.on('PROJECT_LOADED', this.handleProjectLoaded);
    }
    componentWillUnmount () {
        this.props.vm.off('ASSET_PROGRESS', this.handleAssetProgress);
        this.props.vm.runtime.off('PROJECT_LOADED', this.handleProjectLoaded);
    }
    handleAssetProgress (finished, total) {
        if (this.ignoreProgress || !this.barInnerEl || !this.messageEl) {
            return;
        }

        if (total === 0) {
            // Started loading a new project.
            this.barInnerEl.style.width = '0';
            this.messageEl.textContent = this.props.intl.formatMessage(messages.projectData);
        } else {
            this.barInnerEl.style.width = `${finished / total * 100}%`;
            const message = this.props.isRemote ? messages.downloadingAssets : messages.loadingAssets;
            this.messageEl.textContent = this.props.intl.formatMessage(message, {
                complete: finished,
                total
            });
        }
    }
    handleProjectLoaded () {
        if (this.ignoreProgress || !this.barInnerEl || !this.messageEl) {
            return;
        }

        this.ignoreProgress = true;
        this.props.vm.runtime.resetProgress();
    }
    barInnerRef (barInner) {
        this.barInnerEl = barInner;
    }
    messageRef (message) {
        this.messageEl = message;
    }
    render () {
        return (
            <div
                className={classNames(styles.background, {
                    [styles.fullscreen]: this.props.isFullScreen
                })}
            >
                <div className={styles.container}>
                    <div className={styles.berryAnimation}>
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 512 512"
                            className={styles.berryLoader}
                        >
                            {/* 背景 */}
                            <rect className="berry-bg" width="512" height="512" rx="80" fill="#7B2D3F"/>

                            <g transform="translate(56 56)">
                                {/* 草莓主体 */}
                                <g className={styles.berryBody}>
                                    <path d="M200 60c-50 0-90 35-100 80-2 8-5 15-10 20l-15 15c-20 20-20 55 0 75l40 40c20 20 50 30 85 30s65-10 85-30l40-40c20-20 20-55 0-75l-15-15c-5-5-8-12-10-20-10-45-50-80-100-80z" fill="#E63946"/>
                                    <path d="M200 60c-50 0-90 35-100 80-2 8-5 15-10 20l-15 15c-20 20-20 55 0 75l40 40c20 20 50 30 85 30s65-10 85-30l40-40c20-20 20-55 0-75l-15-15c-5-5-8-12-10-20-10-45-50-80-100-80z" fill="none" stroke="#C1121F" strokeWidth="4"/>
                                </g>

                                {/* 白色草莓籽 */}
                                <g className={styles.berrySeedsWhite}>
                                    <circle className={styles.seed1} cx="150" cy="140" r="12" fill="#fff"/>
                                    <circle className={styles.seed2} cx="180" cy="170" r="8" fill="#fff"/>
                                    <circle className={styles.seed3} cx="160" cy="200" r="10" fill="#fff"/>
                                    <circle className={styles.seed4} cx="220" cy="150" r="9" fill="#fff"/>
                                    <circle className={styles.seed5} cx="250" cy="180" r="11" fill="#fff"/>
                                    <circle className={styles.seed6} cx="230" cy="210" r="7" fill="#fff"/>
                                    <circle className={styles.seed7} cx="200" cy="240" r="10" fill="#fff"/>
                                    <circle className={styles.seed8} cx="170" cy="260" r="8" fill="#fff"/>
                                    <circle className={styles.seed9} cx="260" cy="220" r="9" fill="#fff"/>
                                </g>

                                {/* 深红色草莓籽 */}
                                <g className={styles.berrySeedsDark}>
                                    <circle className={styles.seed10} cx="140" cy="180" r="6" fill="#C1121F"/>
                                    <circle className={styles.seed11} cx="200" cy="200" r="5" fill="#C1121F"/>
                                    <circle className={styles.seed12} cx="240" cy="160" r="7" fill="#C1121F"/>
                                    <circle className={styles.seed13} cx="180" cy="240" r="6" fill="#C1121F"/>
                                </g>

                                {/* 绿叶部分 */}
                                <g className={styles.berryLeaf}>
                                    <path d="M200 50c-8 0-15 5-18 12l-5 12c-2 5 1 10 6 12l12 5c5 2 10-1 12-6l5-12c2-5-1-10-6-12l-12-5c-1 0-2 0-3 0z" fill="#2D6A4F"/>
                                    <path d="M200 50c8 0 15 5 18 12l5 12c2 5-1 10-6 12l-12 5c-5 2-10-1-12-6l-5-12c-2-5 1-10 6-12l12-5c1 0 2 0 3 0z" fill="#40916C"/>
                                    <path d="M200 55c-6 0-12 4-14 10l-3 8c-1 4 1 8 5 9l8 3c4 1 8-1 9-5l3-8c1-4-1-8-5-9l-8-3c-1 0-1 0-2 0z" fill="#52B788"/>
                                    <path d="M200 90c0-15 5-25 10-30-3-2-6-3-10-3s-7 1-10 3c5 5 10 15 10 30z" fill="#2D6A4F"/>
                                </g>
                            </g>
                        </svg>
                    </div>

                    <div className={styles.title}>
                        {mainMessages[this.props.messageId]}
                    </div>

                    <div
                        className={styles.message}
                        ref={this.messageRef}
                    />

                    <div className={styles.barOuter}>
                        <div
                            className={styles.barInner}
                            ref={this.barInnerRef}
                        />
                    </div>
                </div>
            </div>
        );
    }
}

LoaderComponent.propTypes = {
    intl: intlShape,
    isFullScreen: PropTypes.bool,
    isRemote: PropTypes.bool,
    messageId: PropTypes.string,
    vm: PropTypes.shape({
        on: PropTypes.func,
        off: PropTypes.func,
        runtime: PropTypes.shape({
            totalAssetRequests: PropTypes.number,
            finishedAssetRequests: PropTypes.number,
            resetProgress: PropTypes.func,
            on: PropTypes.func,
            off: PropTypes.func
        })
    })
};
LoaderComponent.defaultProps = {
    isFullScreen: false,
    messageId: 'gui.loader.headline'
};

const mapStateToProps = state => ({
    isRemote: getIsLoadingWithId(state.scratchGui.projectState.loadingState),
    vm: state.scratchGui.vm
});

const mapDispatchToProps = () => ({});

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(injectIntl(LoaderComponent));
